/* eslint-disable react-hooks/rules-of-hooks */
import { useState, useEffect, useContext, createContext } from 'react';
import PropTypes from 'prop-types';

import { trans, useSoyuzLocales } from '@spotahome/soyuz/client';

import {
  FormSelect,
  FormInput,
  FormDate,
  FormPhoneInput,
  Button,
  Autocompleter,
  FileUploaderMulti
} from '@spotahome/ui-library';
import { FEATURED_CITIES } from '@spotahome/ui-library/src/tenant/components/utils/seoConstants';

import * as yup from 'yup';

import { getAllCities } from '../../graphql/queries/cities';
import graphqlClient from '../../graphql/client';

import FormError from '../FormError';

import './FormProvider.scss';

const FormContext = createContext('FormContext');

const noop = () => {};

const FormProvider = ({
  onSubmit = noop,
  onChange = null,
  onError = noop,
  schema,
  fieldsClassName = 'form-provider__field',
  children,
  disabled = false,
  autocomplete = 'off'
}) => {
  const [values, setValues] = useState({});
  const [errors, setFormErrors] = useState({});

  const validateField = async ({ name, value }) => {
    try {
      await yup.reach(schema, name).validate(value);
      setFormErrors({
        ...errors,
        [name]: undefined
      });
    } catch (error) {
      const { message } = error;
      setFormErrors({
        ...errors,
        [name]: message
      });
    }
  };

  const handleInputChange = async ({ value, name }) => {
    setValues(prevState => ({
      ...prevState,
      [name]: value
    }));

    // if the field has an error, check if new value removes it
    if (errors[name]) {
      await validateField({ name, value });
    }

    if (onChange) {
      onChange(value, name);
    }
  };

  const handleInputBlur = async ({ value, name }) => {
    await validateField({ name, value });
    try {
      await yup.reach(schema, name).validate(value);
      setFormErrors({
        ...errors,
        [name]: undefined
      });
    } catch (error) {
      const { message } = error;
      setFormErrors({
        ...errors,
        [name]: message
      });
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (disabled) {
      return;
    }

    try {
      await schema.validate(values, { abortEarly: false });
      onSubmit(values);
    } catch (error) {
      const newErrors = {};
      if (error && error.inner) {
        error.inner.forEach(errorItem => {
          newErrors[errorItem.path] = errorItem.message;
        });
        setFormErrors(newErrors);
      }
      onError();
    }
  };

  const value = {
    errors,
    values,
    disabled,
    handleInputChange,
    handleInputBlur,
    handleSubmit,
    fieldsClassName
  };

  return (
    <FormContext.Provider value={value}>
      <form onSubmit={handleSubmit} autoComplete={autocomplete}>
        {typeof children === 'function' ? children({ values }) : children}
      </form>
    </FormContext.Provider>
  );
};

FormProvider.Input = ({
  id = 'form-input',
  name,
  title = '',
  placeholder = '',
  type = 'input',
  rows = 1,
  ...otherProps
}) => {
  const { handleInputChange, handleInputBlur, errors, fieldsClassName } =
    useContext(FormContext);
  return (
    <div className={fieldsClassName}>
      <FormInput
        id={id}
        title={title}
        name={name}
        type={type}
        rows={rows}
        placeholder={placeholder}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        required
        {...otherProps}
      />
      <FormError error={trans(errors[name])} />
    </div>
  );
};

FormProvider.Input.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string.isRequired,
  title: PropTypes.string,
  type: PropTypes.oneOf([
    'text',
    'textarea',
    'password',
    'email',
    'number',
    'tel'
  ]),
  placeholder: PropTypes.string,
  rows: PropTypes.number
};

FormProvider.Date = ({ name, title = '', placeholder = '' }) => {
  const {
    handleInputChange,
    handleInputBlur,
    errors,
    fieldsClassName,
    values
  } = useContext(FormContext);
  return (
    <div className={fieldsClassName}>
      <FormDate
        title={title}
        name={name}
        placeholder={placeholder}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        required
        value={values[name]}
      />
      <FormError error={trans(errors[name])} />
    </div>
  );
};

FormProvider.Date.propTypes = {
  name: PropTypes.string.isRequired,
  title: PropTypes.string,
  placeholder: PropTypes.string
};

FormProvider.Submit = ({ isLoading, children }) => {
  const { fieldsClassName, disabled } = useContext(FormContext);

  return (
    <div className={fieldsClassName}>
      <Button
        disabled={disabled || isLoading}
        size="big-redesign"
        color="primary"
        data-test="form-submit"
        type="submit"
      >
        {children}
      </Button>
    </div>
  );
};

FormProvider.Submit.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired
};

FormProvider.Select = ({ name, title = '', placeholder = '', options }) => {
  const {
    handleInputChange,
    handleInputBlur,
    errors,
    fieldsClassName,
    values
  } = useContext(FormContext);

  return (
    <div className={fieldsClassName}>
      <FormSelect
        name={name}
        options={options.map(({ value, text, locoKey }) => ({
          value,
          text: text || trans(locoKey)
        }))}
        title={title}
        placeholder={placeholder}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        required
        value={values[name]}
      />
      <FormError error={trans(errors[name])} />
    </div>
  );
};

FormProvider.Select.propTypes = {
  name: PropTypes.string.isRequired,
  title: PropTypes.string,
  placeholder: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      locoKey: PropTypes.string,
      text: PropTypes.string
    })
  ).isRequired
};

FormProvider.DependentSelectors = ({
  motiveName,
  motiveTitle = '',
  motivePlaceholder = '',
  submotiveName,
  submotiveTitle = '',
  submotivePlaceholder = '',
  options
}) => {
  const {
    handleInputChange,
    handleInputBlur,
    errors,
    fieldsClassName,
    values
  } = useContext(FormContext);

  const [isMotiveSelected, setIsMotiveSelected] = useState(false);
  const [submotiveOptions, setSubmotiveOptions] = useState(
    options[0].submotives
  );

  const handleMotiveChange = e => {
    setIsMotiveSelected(values[motiveName] !== '');

    if (isMotiveSelected) {
      const motive = options.find(option => option.value === e.value);
      setSubmotiveOptions(motive.submotives);
    }
    handleInputChange(e);
  };

  return (
    <>
      <div className={fieldsClassName}>
        <FormSelect
          name={motiveName}
          options={options.map(({ value, text, locoKey }) => ({
            value,
            text: text || trans(locoKey)
          }))}
          title={trans(motiveTitle)}
          placeholder={trans(motivePlaceholder)}
          onChange={handleMotiveChange}
          onBlur={handleInputBlur}
          required
          value={values[motiveName]}
        />
        <FormError error={trans(errors[motiveName])} />
      </div>
      <div className={fieldsClassName}>
        <FormSelect
          name={submotiveName}
          options={submotiveOptions.map(({ value, text, locoKey }) => ({
            value,
            text: text || trans(locoKey)
          }))}
          title={trans(submotiveTitle)}
          placeholder={trans(submotivePlaceholder)}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          required
          value={values[submotiveName]}
          disabled={!isMotiveSelected}
        />
        <FormError error={trans(errors[submotiveName])} />
      </div>
    </>
  );
};

FormProvider.DependentSelectors.propTypes = {
  motiveName: PropTypes.string.isRequired,
  motiveTitle: PropTypes.string,
  motivePlaceholder: PropTypes.string,
  submotiveName: PropTypes.string.isRequired,
  submotiveTitle: PropTypes.string,
  submotivePlaceholder: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      locoKey: PropTypes.string,
      text: PropTypes.string,
      submotives: PropTypes.arrayOf(PropTypes.object)
    })
  ).isRequired
};

FormProvider.CityAutocompleter = ({
  name,
  placeholder = '',
  className = 'city-selector__autocompleter',
  filter = null
}) => {
  const { current: currentLocale } = useSoyuzLocales();
  const {
    values,
    handleInputBlur,
    errors,
    handleInputChange,
    fieldsClassName
  } = useContext(FormContext);
  const [availableCities, setAvailableCities] = useState([]);

  const getDataFromCity = city =>
    availableCities.find(cityData => city === cityData.cityName);

  const handleCitySelected = citySelected => {
    handleInputChange({ value: getDataFromCity(citySelected), name });
  };

  useEffect(() => {
    const getCities = async () => {
      try {
        const citiesResponse = await getAllCities(graphqlClient())(
          currentLocale
        );
        const { cities } = citiesResponse.data;
        setAvailableCities(filter ? cities.filter(filter) : cities);
      } catch (e) {
        console.log('Error fetching cities:', e);
      }
    };
    getCities();
  }, []);

  return (
    <div className={fieldsClassName}>
      <Autocompleter
        list={availableCities.map(city => city.cityName)}
        placeholder={placeholder}
        typingPlaceholder={trans('autocompleter.placeholder.typing-hint')}
        className={className}
        dataTest="city-selector-autocomplete"
        onSelectedElement={handleCitySelected}
        onBlur={handleInputBlur}
        name={name}
        initialValue={values[name] ? values[name].cityName : ''}
        featuredResults={availableCities
          .filter(city => FEATURED_CITIES.includes(city.id))
          .map(city => city.cityName)}
      />
      <FormError error={trans(errors[name])} />
    </div>
  );
};

FormProvider.CityAutocompleter.propTypes = {
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  filter: PropTypes.func
};

FormProvider.PhoneInput = ({ id, name, title, required }) => {
  const {
    handleInputChange,
    handleInputBlur,
    errors,
    fieldsClassName,
    values
  } = useContext(FormContext);

  return (
    <div className={fieldsClassName}>
      <FormPhoneInput
        id={id}
        name={name}
        title={title}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        required={required}
        value={values[name]}
      />
      <FormError error={trans(errors[name])} />
    </div>
  );
};

FormProvider.PhoneInput.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  required: PropTypes.bool.isRequired
};

FormProvider.FileUploaderMulti = ({
  id,
  name,
  title = undefined,
  className = undefined,
  dataTest = undefined,
  required = undefined,
  maxFiles = undefined,
  onFileUploaded = undefined,
  onFileDeleted = undefined,
  onFileUploadError = undefined
}) => {
  const { fieldsClassName, handleInputChange } = useContext(FormContext);

  const handleOnFileUploaded = ({ id: idFile, filePath, files }) => {
    handleInputChange({
      value: files.map(file => file.filePath),
      name
    });

    if (typeof onFileUploaded === 'function') {
      onFileUploaded({ id: idFile, filePath, files });
    }
  };

  const handleOnDelete = ({ id: idFile, filePath, files }) => {
    handleInputChange({
      value: files.map(file => file.filePath),
      name
    });

    if (typeof onFileDeleted === 'function') {
      onFileDeleted({ id: idFile, filePath, files });
    }
  };

  return (
    <div className={fieldsClassName}>
      <FileUploaderMulti
        id={id}
        title={title}
        className={className}
        dataTest={dataTest}
        required={required}
        maxFiles={maxFiles}
        onFileUploaded={handleOnFileUploaded}
        onFileDeleted={handleOnDelete}
        onFileUploadError={onFileUploadError}
      />
    </div>
  );
};

FormProvider.FileUploaderMulti.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  title: PropTypes.string,
  className: PropTypes.string,
  dataTest: PropTypes.string,
  required: PropTypes.bool,
  maxFiles: PropTypes.number,
  onFileUploaded: PropTypes.func,
  onFileDeleted: PropTypes.func,
  onFileUploadError: PropTypes.func
};

FormProvider.propTypes = {
  onSubmit: PropTypes.func,
  onChange: PropTypes.func,
  onError: PropTypes.func,
  schema: PropTypes.shape({
    validate: PropTypes.func
  }).isRequired,
  children: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired,
  fieldsClassName: PropTypes.string,
  disabled: PropTypes.bool,
  autocomplete: PropTypes.string
};

export default FormProvider;
